import React, { useEffect, useState } from "react";
import DocStatusCardsItem from "./DocStatusCardItem";
import { Col, View } from "@amzn/stencil-react-components/layout";
import { DOC_UPLOAD_STATUS_CARDS_VALID_STATE } from "../../../utility/enums/common";
import { H4, Text } from "@amzn/stencil-react-components/text";
import { useChecklistArb } from "../../../hooks/use-arb";
import { useAppDispatch, useAppSelector } from "../../../reduxStore/reduxHooks";
import { getDocStatusCards, updateDocStatusCardsFromEDM } from "../../../reduxStore/slices/docUploadSlice";
import { HVH_COLORS } from "../../../config/palette";
import { useGetDocStatusCardsQuery } from "../../../reduxStore/api/apiSlice";
import { DocStatusCards } from "../../../utility/types/common";
import { getConfigData } from "../../../reduxStore/slices/configSlice";
import { useParams } from "react-router-dom";
import { useActionPath } from "../hooks/useActionPath";
import { skipToken } from "@reduxjs/toolkit/dist/query";

const DocStatusCardList = () => {
  const [savedDocs, setSavedDocs] = useState<DocStatusCards[]>([]);
  const [uncommitedDocs, setUncommitedDocs] = useState<DocStatusCards[]>([]);
  const { applicationId } = useParams<{ applicationId: string }>();
  const actionPath = useActionPath();

  const dispatch = useAppDispatch();
  const docStatusCards = useAppSelector(getDocStatusCards);
  const { candidateData } = useAppSelector(getConfigData);

  const { getMessage } = useChecklistArb();

  // TODO: Change the payload into real data when the API ready
  const { data: docs, refetch } = useGetDocStatusCardsQuery(
    candidateData?.candidateGlobalId
      ? {
          applicationId: applicationId,
          candidateGlobalId: candidateData?.candidateGlobalId,
          actionPath,
        }
      : skipToken
  );

  const updateDocs = (docs: DocStatusCards[]) => {
    const savedDocs: DocStatusCards[] = [];
    const nonSavedDocs: DocStatusCards[] = [];

    docs.forEach((doc) => {
      if (doc.reviewStatus) {
        savedDocs.push(doc);
      } else {
        nonSavedDocs.push(doc);
      }
    });

    setSavedDocs(savedDocs);
    setUncommitedDocs(nonSavedDocs);
  };

  useEffect(() => {
    docs && dispatch(updateDocStatusCardsFromEDM({ documents: docs }));
  }, [docs]);

  useEffect(() => {
    updateDocs(docStatusCards);
  }, [docStatusCards]);

  const renderNonCommitedDocsHeader = () => {
    const hadProcessingStatus = uncommitedDocs.find(
      (doc) => doc.state === DOC_UPLOAD_STATUS_CARDS_VALID_STATE.PROCESSING
    );

    return hadProcessingStatus ? (
      <H4>{getMessage("Checklist-docUpload-status-card-list-header-documents-uploading", "Uploading Docs...")}</H4>
    ) : (
      <H4>{getMessage("Checklist-docUpload-status-card-list-header-documents-uploaded", "Documents to be Saved")}</H4>
    );
  };

  const renderUploadFileRules = (
    <View>
      <Text fontSize={"T100"} color={HVH_COLORS.NEUTRAL_70}>
        {getMessage("Checklist-docUpload-status-card-list-acceptable-format", "Acceptable format: JPEG, JPG, PDF, PNG")}
      </Text>
      <Text fontSize={"T100"} color={HVH_COLORS.NEUTRAL_70} margin={{ top: 5 }}>
        {getMessage("Checklist-docUpload-status-card-list-max-size", "Maximum file size: 7MB")}
      </Text>
    </View>
  );

  const renderSavedDocsHeader = (
    <H4 margin={{ top: "2rem" }}>
      {getMessage("Checklist-docUpload-status-card-list-header-documents-commited", "Saved Documents")}
    </H4>
  );

  // TODO: Need to refactor to single map instead
  const renderUncommitedDocCards = uncommitedDocs
    .filter((doc) => !doc.markForDeletion)
    .map((doc) => <DocStatusCardsItem refetch={refetch} key={doc.documentId} doc={doc} />);

  const renderSavedDocCards = savedDocs
    .filter((doc) => !doc.markForDeletion && doc.actionPath === actionPath)
    .map((doc) => <DocStatusCardsItem refetch={refetch} key={doc.documentId} doc={doc} />);

  return (
    <Col padding="S300" gridGap="S300">
      {renderNonCommitedDocsHeader()}
      {renderUploadFileRules}
      {renderUncommitedDocCards}
      {savedDocs.length > 0 && (
        <>
          {renderSavedDocsHeader}
          {renderSavedDocCards}
        </>
      )}
    </Col>
  );
};

export default DocStatusCardList;
